/**
 * Lightweight markdown renderer. marked (12 kB) + DOMPurify (7 kB) is
 * small enough for a PWA; avoids pulling a heavier library like
 * react-markdown + remark-gfm + rehype-sanitize (~60 kB combined).
 */
import { useMemo } from 'react';
import { marked } from 'marked';
import DOMPurify from 'dompurify';

interface MarkdownContentProps {
  content: string;
}

marked.setOptions({
  gfm: true,
  breaks: true,
});

export function MarkdownContent({ content }: MarkdownContentProps) {
  const html = useMemo(() => {
    const raw = marked.parse(content, { async: false }) as string;
    return DOMPurify.sanitize(raw, {
      USE_PROFILES: { html: true },
      ALLOWED_TAGS: [
        'p',
        'br',
        'strong',
        'em',
        'code',
        'pre',
        'blockquote',
        'ul',
        'ol',
        'li',
        'a',
        'h1',
        'h2',
        'h3',
        'h4',
        'h5',
        'h6',
        'hr',
      ],
      ALLOWED_ATTR: ['href', 'title', 'target', 'rel'],
    });
  }, [content]);

  return (
    <div
      className="prose-sm max-w-none leading-relaxed [&_a]:text-gold [&_a]:underline [&_code]:rounded [&_code]:bg-fog/50 [&_code]:px-1 [&_code]:py-0.5 [&_code]:text-xs [&_pre]:mt-1 [&_pre]:overflow-x-auto [&_pre]:rounded [&_pre]:bg-fog/40 [&_pre]:p-2 [&_pre]:text-xs [&_ul]:ml-4 [&_ul]:list-disc [&_ol]:ml-4 [&_ol]:list-decimal [&_p]:mb-2 last:[&_p]:mb-0"
      // eslint-disable-next-line react/no-danger
      dangerouslySetInnerHTML={{ __html: html }}
    />
  );
}
